import React, { useState, useEffect, useContext } from 'react';
import { TypesDropdown, TypesDropdownOption } from '../TypesDropdown';
import {
  getMemberFromType,
  getParamsForNextDepth,
  getTypeDisplayName,
} from '../utils';
import { NestedTable } from '..';
import { StoreContext } from '../../../../appContext/StoreProvider';
import { areAllTypesTheSame } from '../../utils';
import { getOptionsWithoutDuplicates } from './utils';
import { LegacySingleType, Type } from '../../typeParser';
import { getReferencesToMessagesInType } from '../../../../../utils/typeUtils';

interface MultipleTypesNestedTableProps {
  types: Type[] | LegacySingleType[];
  tableDepth: number;
  selectedTypeIndex: number;
  onTypeDropdownItemSelect: (id: number) => void;
  requiredFields: string[];
  isAutoVeloDocs: boolean;
  JSLayoutType?: boolean;
}

export const MultipleTypesNestedTable = ({
  types,
  tableDepth,
  selectedTypeIndex,
  onTypeDropdownItemSelect,
  requiredFields,
  isAutoVeloDocs,
  JSLayoutType,
  ...restOfProps
}: MultipleTypesNestedTableProps) => {
  const { store } = useContext(StoreContext);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setIsInitialRender(false), 250);
    const AnimateIid = setTimeout(() => setAnimate(true), 100);

    return () => {
      id && clearTimeout(id);
      AnimateIid && clearTimeout(AnimateIid);
    };
  }, []);

  const member = getMemberFromType(types[selectedTypeIndex], store);
  const params = getParamsForNextDepth({
    requiredFields,
    member,
    shouldAddOptionalAnnotation: isAutoVeloDocs,
  });
  if (!params) {
    return null;
  }

  const shouldPresentDropdown = !areAllTypesTheSame(types, store);

  const optionsWithDuplicates = (types as (Type | LegacySingleType)[])
    .map(
      (type, index): TypesDropdownOption => ({
        name: getTypeDisplayName({
          type,
          store,
          // @ts-expect-error TODO
          typeDisplayName: type.displayName,
        }),
        id: index,
      }),
    )
    .filter((_, i) => {
      return getReferencesToMessagesInType(
        [types[i]] as Type[] | LegacySingleType[], // types[i] is from type LegacySingleType | Type
        store,
      ).length;
    });
  const options = getOptionsWithoutDuplicates(optionsWithDuplicates);

  return (
    <React.Fragment>
      {shouldPresentDropdown && (
        <TypesDropdown
          options={options}
          selectedOptionId={selectedTypeIndex}
          onSelect={onTypeDropdownItemSelect}
          styleForDropdown={{
            transform: `translateX(${animate ? 30 * tableDepth : 0}px)`,
          }}
        />
      )}
      <NestedTable
        {...restOfProps}
        forceAddFirstRow={shouldPresentDropdown}
        tableDepth={tableDepth}
        params={params}
        oneOfGroups={member?.extra?.oneOfGroups}
        isAutoVeloDocs={isAutoVeloDocs}
        JSLayoutType={JSLayoutType}
      />
    </React.Fragment>
  );
};
